import React, { useState, useEffect } from 'react';
import {
	Grid,
	AppBar,
	Tabs,
	Tab,
	useMediaQuery,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	IconButton,
} from '@material-ui/core';
import {
	AddBox,
	IndeterminateCheckBox,
	Delete as DeleteIcon,
} from '@material-ui/icons';
import theme from '@ifca-root/react-component/src/assets/theme';
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader';
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper';
import { RetailPOSSearchBar } from './RetailPOSSearchBar';
import { RetailPOSSubheader } from './RetailPOSSubheader';
import { RetailPOSAmountFooter } from './RetailPOSAmountFooter';
import { RetailFooter } from './RetailFooter';
import { ShoppingCartIcon, NoImage } from 'assets';
import { amtStr } from 'helpers/numFormatter';
import './retail.scss';

// Sample data for tabs/categories
const sampleCategories = [
	{ id: 'all', name: 'All' },
	{ id: 'top', name: 'Top' },
	{ id: 'bottoms', name: 'Bottoms' },
	{ id: 'outerwear', name: 'Outerwear' },
	{ id: 'activewear', name: 'Activewear' },
	{ id: 'accessories', name: 'Accessories' },
];

// Sample menu items
const sampleMenuItems = [
	{
		id: 1,
		name: "Men's Oversized T-Shirts",
		description:
			'A relaxed, oversized silhouette made with soft premium cotton. Breathable, durable, and perfect for streetwear or daily comfort.',
		price: 49.9,
		image: null,
		category: 'top',
	},
	{
		id: 2,
		name: "Men's Basic Round Neck Tee",
		description:
			'A must-have everyday tee crafted from lightweight cotton blend. Soft on the skin.',
		price: 35.5,
		image: null,
		category: 'top',
		soldOut: true,
	},
	{
		id: 3,
		name: "Men's Polo Shirts",
		description:
			'A clean and classy polo shirt made with breathable pique fabric. Moisture-wicking to keep you cool.',
		price: 55.5,
		image: null,
		category: 'top',
	},
	{
		id: 4,
		name: "Men's Long Sleeve Tee",
		description: 'A versatile long-sleeve top made from ultra-soft cotton.',
		price: 100.0,
		image: null,
		category: 'top',
	},
	{
		id: 5,
		name: "Men's Hoodies & Sweatshirts",
		description: 'Warm, cozy, and stylish — made with fleece-lined',
		price: 90.0,
		image: null,
		category: 'outerwear',
	},
	{
		id: 6,
		name: "Men's Tank Tops / Gym Tops",
		description:
			'Lightweight, quick-dry tank designed for workouts or hot weather',
		price: 39.5,
		image: null,
		category: 'activewear',
	},
	{
		id: 7,
		name: "Men's Tank Tops / Gym Tops",
		description:
			'Lightweight, quick-dry tank designed for workouts or hot weather',
		price: 38.8,
		image: null,
		category: 'activewear',
	},
	{
		id: 8,
		name: "Men's Tank Tops / Gym Tops",
		description:
			'Lightweight, quick-dry tank designed for workouts or hot weather',
		price: 38.8,
		image: null,
		category: 'activewear',
	},
    {
		id: 8,
		name: "Men's Tank Tops / Gym Tops",
		description:
			'Lightweight, quick-dry tank designed for workouts or hot weather',
		price: 38.8,
		image: null,
		category: 'activewear',
	},
    	{
		id: 8,
		name: "Men's Tank Tops / Gym Tops",
		description:
			'Lightweight, quick-dry tank designed for workouts or hot weather',
		price: 38.8,
		image: null,
		category: 'activewear',
	},
    {
		id: 8,
		name: "Men's Tank Tops / Gym Tops",
		description:
			'Lightweight, quick-dry tank designed for workouts or hot weather',
		price: 38.8,
		image: null,
		category: 'activewear',
	}
];

interface RetailPOSLayoutProps {
	// Main Header Props
	mainHeaderProps?: {
		mainBtn?: string;
		onClick?: () => void;
		smTitle?: string;
		title?: string;
		sideBtn?: string;
		onClickAction?: () => void;
		routeSegments?: any[];
		rightRouteSegments?: any[];
	};

	// Left Panel Props
	leftPanelProps?: {
		tabs?: {
			value: number;
			onChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
			categories: Array<{ name: string; id: string | number }>;
		};
		searchBar?: {
			onChangeAction?: (event: React.ChangeEvent<HTMLInputElement>) => void;
			onCloseAction?: () => void;
			placeholder?: string;
		};
		children: any; // Scrollable content
	};

	// Right Panel Props
	rightPanelProps?: {
		subheader?: {
			title?: any;
			infoLine?: any;
		};
		children: any; // Scrollable content
		footer?: {
			amountFooter?: {
				currencyCode?: string;
				subtotal?: string;
				discount?: string;
				serviceTax?: string;
				total?: string;
				rounding?: string;
				page?: string;
			};
			retailFooter?: {
				options?: Array<{
					visible?: boolean;
					name: string;
					onClick?: () => void;
					color?: 'default' | 'primary' | 'secondary';
					disabled?: boolean;
					startIcon?: React.ReactNode;
				}>;
			};
		};
	};
}

function a11yProps(index: any) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	};
}

export const RetailPOSLayout: React.FC<RetailPOSLayoutProps> = ({
	mainHeaderProps,
	leftPanelProps,
	rightPanelProps,
}) => {


	// Prevent page scroll when component is mounted
	// useEffect(() => {
	// 	document.body.classList.add('retail-pos-no-scroll');
	// 	document.documentElement.classList.add('retail-pos-no-scroll');
		
	// 	return () => {
	// 		document.body.classList.remove('retail-pos-no-scroll');
	// 		document.documentElement.classList.remove('retail-pos-no-scroll');
	// 	};
	// }, []);

	// Default state for example/demo mode
	const [tabValue, setTabValue] = useState(0);
	const [searchValue, setSearchValue] = useState('');
	const [selectedCategory, setSelectedCategory] = useState('all');
	
	// Cart state for demo mode
	const [cartItems, setCartItems] = useState<Array<{
		id: number;
		name: string;
		price: number;
		quantity: number;
		size?: string;
		image?: string | null;
	}>>([
		{
			id: 5,
			name: "Men's Hoodies & Sweatshirts",
			price: 90.0,
			quantity: 1,
			size: 'L',
			image: null,
		},
		{
			id: 6,
			name: "Men's Tank Tops / Gym Tops",
			price: 39.9,
			quantity: 1,
			size: 'M',
			image: null,
		},
        	{
			id: 6,
			name: "Men's Tank Tops / Gym Tops",
			price: 39.9,
			quantity: 1,
			size: 'M',
			image: null,
		},
        	{
			id: 6,
			name: "Men's Tank Tops / Gym Tops",
			price: 39.9,
			quantity: 1,
			size: 'M',
			image: null,
		},
        	{
			id: 6,
			name: "Men's Tank Tops / Gym Tops",
			price: 39.9,
			quantity: 1,
			size: 'M',
			image: null,
		},
        	{
			id: 6,
			name: "Men's Tank Tops / Gym Tops",
			price: 39.9,
			quantity: 1,
			size: 'M',
			image: null,
		},
        	{
			id: 6,
			name: "Men's Tank Tops / Gym Tops",
			price: 39.9,
			quantity: 1,
			size: 'M',
			image: null,
		},
        	{
			id: 6,
			name: "Men's Tank Tops / Gym Tops",
			price: 39.9,
			quantity: 1,
			size: 'M',
			image: null,
		}
        
	]);

	// Default handlers
	const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
		setTabValue(newValue);
		const category = sampleCategories[newValue];
		setSelectedCategory(category?.id || 'all');
		if (leftPanelProps?.tabs?.onChange) {
			leftPanelProps.tabs.onChange(event, newValue);
		}
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.target.value);
		if (leftPanelProps?.searchBar?.onChangeAction) {
			leftPanelProps.searchBar.onChangeAction(event);
		}
	};

	const handleSearchClose = () => {
		setSearchValue('');
		if (leftPanelProps?.searchBar?.onCloseAction) {
			leftPanelProps.searchBar.onCloseAction();
		}
	};

	// Filter items based on category and search (for default example)
	const filteredItems = sampleMenuItems.filter(item => {
		const matchesCategory =
			selectedCategory === 'all' || item.category === selectedCategory;
		const matchesSearch =
			!searchValue ||
			item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
			item.description.toLowerCase().includes(searchValue.toLowerCase());
		return matchesCategory && matchesSearch;
	});

	// Cart handlers
	const handleAddToCart = (item: typeof sampleMenuItems[0]) => {
		setCartItems(prev => {
			const existingItem = prev.find(cartItem => cartItem.id === item.id);
			if (existingItem) {
				return prev.map(cartItem =>
					cartItem.id === item.id
						? { ...cartItem, quantity: cartItem.quantity + 1 }
						: cartItem
				);
			}
			return [
				...prev,
				{
					id: item.id,
					name: item.name,
					price: item.price,
					quantity: 1,
					size: 'M', // Default size
					image: item.image,
				},
			];
		});
	};

	const handleQuantityChange = (itemId: number, action: 'increase' | 'decrease') => {
		setCartItems(prev =>
			prev.map(item => {
				if (item.id === itemId) {
					if (action === 'increase') {
						return { ...item, quantity: item.quantity + 1 };
					} else {
						return { ...item, quantity: Math.max(1, item.quantity - 1) };
					}
				}
				return item;
			})
		);
	};

	const handleRemoveItem = (itemId: number) => {
		setCartItems(prev => prev.filter(item => item.id !== itemId));
	};

	const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
	const totalAmount = cartItems.reduce(
		(sum, item) => sum + item.price * item.quantity,
		0
	);

	// Use provided props or defaults
	const finalMainHeaderProps =
		mainHeaderProps ||
		({
			mainBtn: 'menu',
			onClick: () => console.log('Menu clicked'),
			smTitle: 'Outlet App',
			title: 'Run & Rise Apparel',
			sideBtn: 'switch',
			onClickAction: () => console.log('Switch clicked'),
			routeSegments: [{ name: 'Table Layout', current: true }],
			rightRouteSegments: [
				{
					name: 'No applicable meal period at the moment',
					current: true,
				},
			],
		} as any);

	const finalLeftPanelProps = leftPanelProps || {
		tabs: {
			value: tabValue,
			onChange: handleTabChange,
			categories: sampleCategories,
		},
		searchBar: {
			onChangeAction: handleSearchChange,
			onCloseAction: handleSearchClose,
		},
		children: (
			<>
				<List className="core-list" style={{ marginBottom: '0px' }}>
					{filteredItems.map(item => (
						<ListItem
							key={item.id}
							style={{
								padding: '8px 12px',
								backgroundColor: item.soldOut ? '#ffc0c0' : null,
							}}
							disabled={item.soldOut}
						>
							<div style={{ paddingRight: '6px' }}>
								<ListItemIcon>
									<img
										className="img-owned"
										style={{ border: 'none' }}
										src={NoImage}
										alt={item.name}
									/>
								</ListItemIcon>
								{item.soldOut && (
									<div className="retail-img-sold-out-text">SOLD</div>
								)}
							</div>
							<ListItemText
								primary={
									<span
										className="xsTitle"
										style={{
											overflowWrap: 'break-word',
											whiteSpace: 'normal',
											color: '#000',
										}}
									>
										{item.name}
									</span>
								}
								secondary={
									<span className="flex-space desc">
										<div>{item.description}</div>
										<div>
											<span className="highlight-text">
												RM {amtStr(item.price)}
											</span>
										</div>
									</span>
								}
							/>
							<IconButton
								style={{ padding: '0px' }}
								disabled={item.soldOut}
								onClick={() => handleAddToCart(item)}
							>
								<AddBox
									style={{
										fontSize: '32px',
										paddingTop: '28px',
										color: '#ff9800',
									}}
								/>
							</IconButton>
						</ListItem>
					))}
				</List>
			</>
		),
	};

	const finalRightPanelProps = rightPanelProps || {
		subheader: {
			title: (
				<span className="xsTitle color-primary-orange">Order Summary</span>
			),
			infoLine: (
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						width: '100%',
					}}
				>
					<span className="desc">#ORD00001</span>
					<span className="desc right-info">
						Total Item: {totalItems}
					</span>
				</div>
			),
		},
		children: (
			<>
				{cartItems.length === 0 ? (
					<List
						style={{
							backgroundColor: 'white',
							color: 'black',
							width: '100%',
						}}
					>
						<div className="empty-listing">
							<ListItemIcon>
								<img src={ShoppingCartIcon} alt="Empty cart" />
							</ListItemIcon>
							<div className="smTitle">No Item in the cart</div>
							<div className="desc">Add on item manually or</div>
							<div className="desc">scan a barcode to continue</div>
						</div>
					</List>
				) : (
					<List className="retail-pos-order-items">
						{cartItems.map(item => (
							<ListItem
								key={item.id}
								className="retail-pos-order-item"
								style={{ padding: '12px 16px' }}
							>
								<div style={{ paddingRight: '12px' }}>
									<ListItemIcon style={{ minWidth: '60px' }}>
										<img
											className="img-owned"
											style={{
												width: '60px',
												height: '60px',
												objectFit: 'cover',
												borderRadius: '4px',
												border: 'none',
											}}
											src={item.image || NoImage}
											alt={item.name}
										/>
									</ListItemIcon>
								</div>
								<ListItemText
									style={{ flex: 1 }}
									primary={
										<span
											className="xsTitle"
											style={{
												fontWeight: 600,
												color: '#000',
												display: 'block',
												marginBottom: '4px',
											}}
										>
											{item.name}
										</span>
									}
									secondary={
										<span className="desc" style={{ fontSize: '12px' }}>
											Size: {item.size}
										</span>
									}
								/>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'flex-end',
										gap: '8px',
									}}
								>
									<div
										style={{
											textAlign: 'right',
										}}
									>
										<span
											className="highlight-text"
											style={{ color: '#ff9800', fontWeight: 600, fontSize: '14px' }}
										>
											{amtStr(item.price * item.quantity)}
										</span>
									</div>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '6px',
										}}
									>
									<IconButton
										className="retail-pos-qty-btn retail-pos-qty-minus"
										onClick={() => handleQuantityChange(item.id, 'decrease')}
									>
										<IndeterminateCheckBox />
									</IconButton>
									<div className="retail-pos-qty-display">{item.quantity}</div>
									<IconButton
										className="retail-pos-qty-btn retail-pos-qty-plus"
										onClick={() => handleQuantityChange(item.id, 'increase')}
									>
										<AddBox />
									</IconButton>
										<IconButton
											className="retail-pos-qty-btn retail-pos-qty-delete"
											onClick={() => handleRemoveItem(item.id)}
										>
											<DeleteIcon style={{ fontSize: '16px' }} />
										</IconButton>
									</div>
								</div>
							</ListItem>
						))}
					</List>
				)}
			</>
		),
		footer: {
			amountFooter: {
				currencyCode: 'RM',
				subtotal: '0.00',
				discount: '0.00',
				serviceTax: '0.00',
				total: '0.00',
				rounding: '0.00',
				page: 'payment',
			},
			retailFooter: {
				options: [
					{
						visible: true,
						name: 'Print',
						onClick: () => console.log('Print clicked'),
						color: 'primary' as const,
						disabled: true,
					},
					{
						visible: true,
						name: 'Payment',
						onClick: () => console.log('Payment clicked'),
						color: 'primary' as const,
						disabled: true,
					},
				],
			},
		},
	};

	return (
		<>
			{/* Main Header */}
			<MainHeader
				mainBtn={finalMainHeaderProps.mainBtn}
				onClick={finalMainHeaderProps.onClick}
				smTitle={finalMainHeaderProps.smTitle}
				title={finalMainHeaderProps.title}
				sideBtn={finalMainHeaderProps.sideBtn}
				onClickAction={finalMainHeaderProps.onClickAction}
				routeSegments={finalMainHeaderProps.routeSegments}
				rightRouteSegments={finalMainHeaderProps.rightRouteSegments}
			/>

			{/* Main Container with Grid */}
			<div className="retail-pos-container">
				<Grid container className="retail-pos-grid-container">
					{/* Left Panel - 7 columns */}
					<Grid item xs={12} sm={7} className="retail-pos-left-panel" style={{ padding: 0 }}>
						{/* Fixed Header: Tabs + Search Bar */}
						<div className="retail-pos-left-header">
							{/* Tabs */}
							{finalLeftPanelProps.tabs && (
								<div className="retail-pos-tabs-container">
									<AppBar position="static" style={{ boxShadow: 'none' }}>
										<Tabs
											aria-label="category tabs"
											indicatorColor="primary"
											textColor="primary"
											value={finalLeftPanelProps.tabs.value}
											onChange={finalLeftPanelProps.tabs.onChange}
											variant="scrollable"
											scrollButtons="auto"
										>
											{finalLeftPanelProps.tabs.categories.map((category, index) => (
												<Tab
													key={category.id}
													label={category.name}
													{...a11yProps(index)}
												/>
											))}
										</Tabs>
									</AppBar>
								</div>
							)}

							{/* Search Bar */}
							{finalLeftPanelProps.searchBar && (
								<div className="retail-pos-search-container">
									<RetailPOSSearchBar
										onChangeAction={finalLeftPanelProps.searchBar.onChangeAction}
										onCloseAction={finalLeftPanelProps.searchBar.onCloseAction}
										placeholder={
											finalLeftPanelProps.searchBar.placeholder ||
											'Search here..'
										}
									/>
								</div>
							)}
						</div>

						{/* Scrollable Content */}
						<div className="retail-pos-left-content">
							{finalLeftPanelProps.children}
						</div>
					</Grid>

					{/* Right Panel - 5 columns */}
					<Grid item xs={12} sm={5} className="retail-pos-right-panel" style={{ padding: 0 }}>
						{/* Fixed Subheader */}
						{finalRightPanelProps.subheader && (
							<div className="retail-pos-right-header">
								<RetailPOSSubheader
									title={finalRightPanelProps.subheader.title}
									infoLine={finalRightPanelProps.subheader.infoLine}
								/>
							</div>
						)}

						{/* Scrollable Content */}
						<div className="retail-pos-right-content">
							{finalRightPanelProps.children}
						</div>

						{/* Fixed Footer */}
						{finalRightPanelProps.footer && (
							<div className="retail-pos-right-footer">
								{finalRightPanelProps.footer.amountFooter && (
									<RetailPOSAmountFooter
										currencyCode={
											finalRightPanelProps.footer.amountFooter.currencyCode || ''
										}
										subtotal={finalRightPanelProps.footer.amountFooter.subtotal || '0'}
										discount={
											finalRightPanelProps.footer.amountFooter.discount || '0'
										}
										serviceTax={
											finalRightPanelProps.footer.amountFooter.serviceTax || '0'
										}
										total={finalRightPanelProps.footer.amountFooter.total || '0'}
										rounding={
											finalRightPanelProps.footer.amountFooter.rounding || '0'
										}
										page={finalRightPanelProps.footer.amountFooter.page || 'payment'}
									/>
								)}
								{finalRightPanelProps.footer.retailFooter && (
									<RetailFooter
										options={
											finalRightPanelProps.footer.retailFooter.options?.filter(
												x => x?.visible !== false,
											) || []
										}
									/>
								)}
							</div>
						)}
					</Grid>
				</Grid>
			</div>
		</>
	);
};

export default RetailPOSLayout;

